/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TemplateDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import UpdateIcon from "@mui/icons-material/Update";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CustomButton from "../buttons/CustomButton";
import customConsole from "../../config/customConsole";
import DynamicFormTemplate from "./DynamicFormTemplate";
import generatePDF from "react-to-pdf";
import typeApprovalAPI from "../../redux/actions/typeApprovalAPI";
import { HandleApiActions } from "../../redux/actions";
import { useDispatch } from "react-redux";
import { FORM_TYPES } from "../../config/appConstants";
import CustomLogsDialog from "../dialog-boxes/CustomLogsDialog";

const TemplateDialog = ({
  open = false,
  onClose = () => {},
  ticketId = "",
  dialogTitle = "",
  dynamicFormData = null,
  setDynamicFormData = () => {},
}) => {
  const dispatch = useDispatch();

  const templateRef = useRef();

  const ALIGN_TYPES = { LEFT: "left", CENTER: "center", RIGHT: "right" };

  const [selectedHeaders, setSelectedHeaders] = useState([ALIGN_TYPES?.CENTER]);
  const [selectedFooters, setSelectedFooters] = useState([ALIGN_TYPES?.CENTER]);
  const [selectedSignatures, setSelectedSignatures] = useState([
    ALIGN_TYPES?.RIGHT,
  ]);

  const [openTemplateChangeLogsDialog, setOpenTemplateChangeLogsDialog] =
    useState(false);
  const [templateChangeLogsList, setTemplateChangeLogsList] = useState([]);
  const [templateLogsDialogData, setTemplateLogsDialogData] = useState({});

  const [updatedFieldsValues, setUpdatedFieldsValues] = useState([]);
  const [currentFieldValue, setCurrentFieldValue] = useState({
    field_name: "",
    value: "",
    default_value: "",
    edit_type: "",
    sequence: "",
    reason: "",
  });
  const [showUpdateFieldValues, setShowUpdateFieldValues] = useState(false);

  const headerOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Center",
      position: ALIGN_TYPES?.CENTER,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const footerOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Center",
      position: ALIGN_TYPES?.CENTER,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const signatureOptionsList = [
    {
      label: "Left",
      position: ALIGN_TYPES?.LEFT,
      value: "",
    },
    {
      label: "Right",
      position: ALIGN_TYPES?.RIGHT,
      value: "",
    },
  ];

  const getFormTypeText = (type) => {
    if (type === FORM_TYPES?.EE) return "EE Component";
    if (type === FORM_TYPES?.SWM) return "Software Module";
    if (type === FORM_TYPES?.VEHICLE) return "Vehicle Component";
    return "-";
  };

  const handleGetTemplateChangeLogs = async ({ pageNo, pageSize }) => {
    let apiDetails = typeApprovalAPI.get_dynamic_form_logs;

    if (dynamicFormData?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(dynamicFormData?.form_id),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      const resp = dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(dynamicFormData?.form_id),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );
      if (resp?.error) {
        setTemplateChangeLogsList([]);
      } else {
        setTemplateChangeLogsList(resp?.data);
      }
    }
  };

  const handleTemplateLogsDialogOpen = (data) => {
    setOpenTemplateChangeLogsDialog(true);

    setTemplateLogsDialogData(data);
  };
  const handleTemplateLogsDialogClose = () => {
    setTemplateLogsDialogData(null);
    setTemplateChangeLogsList([]);

    setOpenTemplateChangeLogsDialog(false);
  };

  const updateFieldValues = async () => {
    let apiDetails = typeApprovalAPI.update_dynamic_form;

    if (dynamicFormData?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(dynamicFormData?.form_id),
          field_details: updatedFieldsValues,
        },
        show_toast: true,
      });

      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(dynamicFormData?.form_id),
            field_details: updatedFieldsValues,
          },
          show_toast: true,
        }),
      );
    }

    setUpdatedFieldsValues([]);
  };

  const resetFieldValuesInputs = () => {
    setCurrentFieldValue({
      field_name: "",
      value: "",
      default_value: "",
      edit_type: "",
      sequence: "",
      reason: "",
    });

    setShowUpdateFieldValues(false);
  };

  const handleUpdateFieldValues = () => {
    if (currentFieldValue?.sequence) {
      setUpdatedFieldsValues((prev) => [
        ...prev,
        {
          field_name: currentFieldValue?.field_name,
          value: currentFieldValue?.value,
          reason: currentFieldValue?.reason,
        },
      ]);

      setDynamicFormData((prev) => ({
        ...prev,
        fields: prev?.fields?.map((field1) =>
          field1?.sequence === currentFieldValue?.sequence
            ? { ...field1, value: currentFieldValue?.value }
            : field1,
        ),
      }));
    }

    resetFieldValuesInputs();
  };

  const handleFieldValueChange = (field, value) => {
    setCurrentFieldValue((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditUpdateField = (fieldName) => {
    let fieldToUpdate = dynamicFormData?.fields?.find(
      (field1) => field1?.field_name === fieldName,
    );

    if (fieldToUpdate) {
      setCurrentFieldValue(fieldToUpdate);
      setShowUpdateFieldValues(true);
    }
  };

  useEffect(() => {
    const validPositions = ["left", "center", "right"];
    const validPositions1 = ["left", "right"];

    let selectedHeadersList =
      dynamicFormData?.headers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedHeaders(selectedHeadersList);

    let selectedSignaturesList =
      dynamicFormData?.signatures
        ?.map((el) =>
          validPositions1?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedSignatures(selectedSignaturesList);

    let selectedFootersList =
      dynamicFormData?.footers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedFooters(selectedFootersList);
  }, [open, dynamicFormData]);

  useEffect(() => {
    customConsole({ dynamicFormData17: dynamicFormData });
  }, [dynamicFormData]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={"md"}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "center",
          boxShadow: 2,
          py: 1,
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {dialogTitle}
        </Typography>

        <IconButton
          edge="end"
          color="inherit"
          onClick={() => {
            onClose();
            resetFieldValuesInputs();
            setUpdatedFieldsValues([]);
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {dynamicFormData?.intr_id ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -2 }}>
              <Tooltip title="Change logs">
                <IconButton
                  size="small"
                  sx={{ color: "green" }}
                  onClick={() =>
                    handleTemplateLogsDialogOpen({ pageNo: 1, pageSize: 25 })
                  }
                >
                  <HistoryEduIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download template">
                <IconButton
                  size="small"
                  sx={{ color: "green" }}
                  onClick={() =>
                    generatePDF(templateRef, { filename: "page.pdf" })
                  }
                >
                  <FileDownloadIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Box>
            <Paper elevation={2} sx={styles.intDetailsContainer}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  textAlign: "center",
                  pt: 0.4,
                }}
              >
                Form Details
              </Typography>
              <Divider sx={{ mt: 0.4, mb: 1 }} />
              <Grid container sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Intersection id: </span>
                      {dynamicFormData?.intr_id || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Ticket id: </span>
                      {ticketId || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Form name: </span>
                      {dynamicFormData?.name || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={styles.intDetailsTxt}>
                      <span style={styles.txtBold}>Form type: </span>
                      {getFormTypeText(dynamicFormData?.type)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            <Box sx={{ display: "flex", gap: 2.5 }}>
              <Box
                ref={templateRef}
                sx={{
                  minHeight: "315mm",
                  padding: "10px",
                  display: "flex",
                  textDecoration: "none",
                  justifyContent: "space-between",
                }}
              >
                <DynamicFormTemplate
                  showOnlyTemplate={true}
                  handleEditField={() => { }}
                  showFieldsUpdate={true}
                  handleEditUpdateField={handleEditUpdateField}
                  headerOptionsList={headerOptionsList}
                  signatureOptionsList={signatureOptionsList}
                  footerOptionsList={footerOptionsList}
                  selectedHeaders={selectedHeaders}
                  selectedSignatures={selectedSignatures}
                  selectedFooters={selectedFooters}
                  dynamicFormData={dynamicFormData}
                  setDynamicFormData={setDynamicFormData}
                />
              </Box>
              {showUpdateFieldValues ? (
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    px: 1,
                    py: 1.4,
                    border: "1px solid grey",
                    borderRadius: 1,
                    height: "262px",
                    width: "240px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "17px",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Update Field
                  </Typography>
                  <Divider
                    sx={{ m: -1, p: 0, border: "1px solid lightgrey" }}
                  />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="field-name"
                    >
                      field name*
                    </Typography>
                    <TextField
                      fullWidth
                      id="field-name"
                      variant="standard"
                      type={"text"}
                      size={"small"}
                      disabled={true}
                      value={currentFieldValue?.field_name || ""}
                      onChange={() => { }}
                      sx={{ width: "220px", mt: -0.7 }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="field-value"
                    >
                      Enter field value*
                    </Typography>
                    <TextField
                      fullWidth
                      id="field-value"
                      variant="standard"
                      type={"text"}
                      size={"small"}
                      value={currentFieldValue?.value || ""}
                      onChange={(e) =>
                        handleFieldValueChange("value", e.target?.value)
                      }
                      sx={{ width: "220px", mt: -0.7 }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      color={"grey"}
                      component="label"
                      htmlFor="reason"
                    >
                      Enter reason*
                    </Typography>
                    <TextField
                      fullWidth
                      id="reason"
                      variant="standard"
                      type={"text"}
                      size={"small"}
                      value={currentFieldValue?.reason || ""}
                      onChange={(e) =>
                        handleFieldValueChange("reason", e.target?.value)
                      }
                      sx={{ width: "220px", mt: -0.7 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1.4,
                      mt: 2,
                    }}
                  >
                    <CustomButton
                      size={"small"}
                      variant={"contained"}
                      btnName={"Close"}
                      startIcon={<CloseIcon />}
                      handleOnClick={resetFieldValuesInputs}
                      btnStyle={{}}
                    />
                    <CustomButton
                      size={"small"}
                      variant={"contained"}
                      btnName={"Update"}
                      disabled={
                        !(
                          currentFieldValue?.field_name?.length &&
                          currentFieldValue?.value?.length &&
                          currentFieldValue?.reason?.length
                        )
                      }
                      startIcon={<UpdateIcon />}
                      handleOnClick={handleUpdateFieldValues}
                      btnStyle={{}}
                    />
                  </Box>
                </Paper>
              ) : null}
            </Box>
            <CustomLogsDialog
              open={openTemplateChangeLogsDialog}
              handleClose={handleTemplateLogsDialogClose}
              dialogTitle={"Change Logs"}
              dialogContent={templateChangeLogsList}
              data={templateLogsDialogData}
              getLogsData={handleGetTemplateChangeLogs}
            />
          </>
        ) : (
          <Box
            style={{
              height: "370px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">No attachments found</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ boxShadow: 4, gap: 2, px: 2, py: 1 }}>
        <CustomButton
          size={"small"}
          variant={"contained"}
          btnName={"Close"}
          startIcon={<CloseIcon />}
          handleOnClick={() => {
            onClose();
            resetFieldValuesInputs();
            setUpdatedFieldsValues([]);
          }}
          btnStyle={{}}
        />
        {dynamicFormData?.intr_id ? (
          <CustomButton
            size={"small"}
            variant={"contained"}
            btnName={"Update"}
            startIcon={<UpdateIcon />}
            disabled={
              !updatedFieldsValues?.length ||
              !updatedFieldsValues?.every(
                (item) => item?.field_name && item?.value && item?.reason,
              )
            }
            handleOnClick={() => {
              onClose();
              resetFieldValuesInputs();
              updateFieldValues();
            }}
            btnStyle={{}}
          />
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

TemplateDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dialogTitle: PropTypes.node,
  dynamicFormData: PropTypes.shape({
    form_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    intr_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        field_name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        default_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        edit_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sequence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        reason: PropTypes.string,
      })
    ),
    headers: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      })
    ),
    signatures: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      })
    ),
    footers: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.string,
      })
    ),
  }),
  setDynamicFormData: PropTypes.func,
};

export default TemplateDialog;

const styles = {
  intDetailsContainer: {
    px: 2,
    height: "100%",
    border: "1px solid grey",
    borderRadius: 1,
  },
  intDetailsTxt: {
    textAlign: "start",
    alignContent: "center",
  },
  txtBold: {
    fontWeight: "bold",
  },
};

